import React, { useRef, useImperativeHandle, forwardRef } from "react";
import useCropOverlay from "./Utilities/CropOverlay";
import cropMedia from "./Utilities/CropMedia";
import ImageViewer from "./Utilities/ImageViewer";
import VideoViewer from "./Utilities/VideoViewer";
import PdfViewer from "./Utilities/PdfViewer";

/**
 * MediaViewer — routes to the correct sub-viewer and manages crop overlay.
 *
 * Props:
 *   type       : "image" | "video" | "pdf"
 *   src        : renderable URL
 *   name       : filename for alt text
 *   onCropDone : (croppedDataUrl: string) => void
 *
 * Ref methods (called by parent):
 *   ref.current.startCrop()    — show the crop overlay
 *   ref.current.confirmCrop()  — execute the crop and fire onCropDone
 *   ref.current.cancelCrop()   — hide the overlay without cropping
 */

const MediaViewer = forwardRef(function MediaViewer(
  { type, src, name = "media", onCropDone },
  ref
) {
  const containerRef = useRef(null);
  const childRef = useRef(null);

  const {
    cropState,
    startCrop,
    cancelCrop,
    getCropRect,
    onPointerDownBox,
  } = useCropOverlay();

  useImperativeHandle(ref, () => ({
    startCrop,
    cancelCrop,
    confirmCrop: async () => {
      try {
        const mediaEl = childRef.current?.getMediaEl();
        const rect = getCropRect();
        const croppedDataUrl = await cropMedia(
          mediaEl,
          containerRef.current,
          rect
        );
        cancelCrop();
        onCropDone?.(croppedDataUrl);
      } catch (err) {
        console.error("Crop failed:", err);
        cancelCrop();
      }
    },
    getExportedDataUrl: () => childRef.current?.getExportedDataUrl?.(),
  }), [startCrop, cancelCrop, getCropRect, onCropDone]);

  if (!src) {
    return (
      <div style={styles.container}>
        <span style={styles.empty}>No media to display</span>
      </div>
    );
  }

  return (
    <div style={styles.container} ref={containerRef}>

      {type === "image" && (
        <ImageViewer ref={childRef} src={src} name={name} />
      )}

      {type === "video" && (
        <VideoViewer ref={childRef} src={src} name={name} />
      )}

      {type === "pdf" && (
        <PdfViewer
          ref={childRef}
          src={src}
          name={name}
          cropActive={cropState.active}
        />
      )}

      {/* Crop overlay — shared across all media types */}
      {cropState.active && (
        <>
          <div style={styles.overlayBackdrop} />

          <div
            onPointerDown={onPointerDownBox}
            style={{
              position: "absolute",
              left: cropState.x,
              top: cropState.y,
              width: cropState.width,
              height: cropState.height,
              border: "2px solid #fff",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
              cursor: "move",
              zIndex: 11,
              boxSizing: "border-box",
            }}
          >
            {["n", "s", "e", "w", "ne", "nw", "se", "sw"].map((handle) => (
              <div
                key={handle}
                data-handle={handle}
                onPointerDown={onPointerDownBox}
                style={{
                  position: "absolute",
                  ...getHandlePosition(handle),
                  width: handle.length === 2 ? 12 : ["n", "s"].includes(handle) ? 30 : 8,
                  height: handle.length === 2 ? 12 : ["e", "w"].includes(handle) ? 30 : 8,
                  background: "#fff",
                  borderRadius: 2,
                  cursor: `${handle}-resize`,
                  zIndex: 12,
                  transform: "translate(-50%, -50%)",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
});

export default MediaViewer;

function getHandlePosition(handle) {
  const pos = {};
  if (handle.includes("n")) pos.top = 0;
  if (handle.includes("s")) pos.top = "100%";
  if (handle.includes("e")) pos.left = "100%";
  if (handle.includes("w")) pos.left = 0;
  if (handle === "n" || handle === "s") pos.left = "50%";
  if (handle === "e" || handle === "w") pos.top = "50%";
  return pos;
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    overflow: "hidden",
    position: "relative",
  },
  empty: {
    color: "#9ca3af",
    fontSize: "14px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  overlayBackdrop: {
    position: "absolute",
    inset: 0,
    zIndex: 10,
    pointerEvents: "none",
  },
};